import { useEffect, useState } from 'react';
import { useStoreState } from './store';
import * as api from './fetch';

const CDN_URL = import.meta.env.VITE_APP_LOCALE_CDN as string;

const getCurrentLocale = (state: any) => state?.auth?.user?.locale ?? 'en';

async function cdn() {
  try {
    const response = await fetch(CDN_URL);
    if (!response.ok) {
      throw new Error(
        `CDN request failed: ${response.status} ${response.statusText}`
      );
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to load translations from CDN:', error);
    // Return empty object, React Intl will use defaultMessage props
    return {};
  }
}

export function useLocaleProvider() {
  const locale = useStoreState(getCurrentLocale);
  const [messages, setMessages] = useState<Record<string, any>>({});

  useEffect(() => {
    cdn()
      .then(updated => {
        setMessages(updated || {});
      })
      .catch(() => {
        // Final fallback - empty messages, React Intl will use defaultMessage
        setMessages({});
      });
  }, []);

  return {
    messages: messages[locale] ?? {},
    locale,
  };
}

export const LANGUAGES = {
  cn: {
    name: '中文(简体)',
    dayjsLocaleLoader: () => import('dayjs/locale/zh-cn'),
    dayjsLocaleId: 'zh-cn',
    reactIntlLocale: 'zh-CN',
  },
  de: {
    name: 'German',
    dayjsLocaleLoader: () => import('dayjs/locale/de'),
    reactIntlLocale: 'de-DE',
  },
  en: {
    name: 'English',
    dayjsLocaleLoader: () => import('dayjs/locale/en'),
    reactIntlLocale: 'en-US',
  },
  es: {
    name: 'Spanish',
    dayjsLocaleLoader: () => import('dayjs/locale/es'),
    reactIntlLocale: 'es-ES',
  },
  fr: {
    name: 'French',
    dayjsLocaleLoader: () => import('dayjs/locale/fr'),
    reactIntlLocale: 'fr-FR',
  },
  pt: {
    name: 'Portuguese',
    dayjsLocaleLoader: () => import('dayjs/locale/pt'),
    reactIntlLocale: 'pt-PT',
  },
  tw: {
    name: '中文(繁體)',
    dayjsLocaleLoader: () => import('dayjs/locale/zh-tw'),
    dayjsLocaleId: 'zh-tw',
    reactIntlLocale: 'zh-TW',
  },
};
if (import.meta.env.VITE_APP_ENV !== 'prod') {
  LANGUAGES.ja = {
    name: 'TEST LANGUAGE',
    dayjsLocaleLoader: () => import('dayjs/locale/ja'),
    reactIntlLocale: 'ja-JP',
  };
}

export function useLocale() {
  const current = useStoreState(getCurrentLocale);
  const [loading, setLoading] = useState(false);

  async function change(locale: string) {
    setLoading(true);

    try {
      const response = await api.user.updateLocale(locale);
      return await api.utils.processResponse(response, {
        200() {
          setLoading(false);
          return { success: true } as const;
        },
        default() {
          setLoading(false);
          return { success: false } as const;
        },
      });
    } catch (error) {
      setLoading(false);
      return { success: false } as const;
    }
  }

  const options = Object.entries(LANGUAGES).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );

  return { current, loading, options, change };
}
